/**
 * PhotoGalleryModal Component
 *
 * Diamond Architecture: Fullscreen photo gallery matching Rails Listings::PhotoGalleryModalComponent.
 * Pattern: Vertical scroll of all images (no carousel navigation).
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { plainText, type LocalizedText, type S3Key } from '@letsgethome/types/branded';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@letsgethome/ui/dialog';
import { cn } from '@letsgethome/ui/lib/utils';
import { S3Image } from '@letsgethome/ui/s3-image';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

interface PhotoGalleryModalProps {
  images: S3Key[];
  alt: LocalizedText | '';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PhotoGalleryModal({ images, alt, open, onOpenChange }: PhotoGalleryModalProps) {
  const t = useTranslations('listings.gallery');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // Mobile: fullscreen
          'top-0 left-0 translate-x-0 translate-y-0 w-full h-full max-w-full sm:max-w-full max-h-full rounded-none border-0',
          // Desktop: centered dialog
          'md:top-[50%] md:left-[50%] md:translate-x-[-50%] md:translate-y-[-50%] md:h-auto md:max-w-3xl md:max-h-[90vh] md:rounded-lg md:border',
          // Shared
          'p-0 gap-0 flex flex-col overflow-hidden'
        )}
      >
        <DialogHeader
          className={cn(
            'px-6 py-4 border-b sticky top-0 bg-background',
            Z_LAYERS.stickyPanelHeader.className
          )}
        >
          <DialogTitle className="text-center">{t('title')}</DialogTitle>
        </DialogHeader>

        {/* Scrollable image stack */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-4">
            {images.map((image, index) => (
              <div key={index} className="relative w-full">
                <S3Image
                  src={image}
                  alt={plainText(`${alt} - ${index + 1}`)}
                  width={768}
                  height={512}
                  className={cn('w-full h-auto rounded-md bg-muted object-cover')}
                  sizes="(max-width: 768px) 100vw, 768px"
                  preload={index === 0}
                />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
